import { getModKeyLabel } from '../utils/keyboard';

/**
 * Single source of truth for app keyboard shortcuts.
 *
 * keys spec: { mod?, ctrl?, meta?, shift?, alt?, key }
 *   - `mod`  = Ctrl on Windows/Linux OR ⌘ on macOS (platform agnostic)
 *   - `ctrl` / `meta` = literal modifier (used for platform-specific binds, e.g. devtools)
 *   - `key`  = KeyboardEvent.key (case-insensitive); e.g. 'k', 'Enter', '`', 'F2'
 *
 * Each command:
 *   - id              stable identifier (also the override key in preferences)
 *   - labelKey        i18n key for the display label
 *   - defaultKeys     default binding (null = unbound by default)
 *   - editable        whether the user can remap it
 *   - paletteActionId command-palette actionId this maps to, if any
 *   - altKeys         fixed secondary binding that always works (not remappable)
 *   - group           which section of Settings > Keyboard it appears under
 *
 * `editable: false` means the binding is hard-coded in whatever component
 * handles it and does not go through matchesShortcut. Do not flip one to true
 * without also routing its handler through matchesShortcut, or the Settings row
 * would accept a new combo that nothing listens for.
 */
/** Sections of Settings > Keyboard, in display order. Extension-specific
 * shortcuts are grouped under the extension they belong to. */
export const SHORTCUT_GROUP_BASE = 'base';
export const SHORTCUT_GROUP_DB_MANAGER = 'dbManager';
export const SHORTCUT_GROUP_TERMINAL = 'terminal';

export const SHORTCUT_GROUPS = [
  { id: SHORTCUT_GROUP_BASE, labelKey: 'settings.shortcutGroupBase' },
  { id: SHORTCUT_GROUP_DB_MANAGER, labelKey: 'settings.shortcutGroupDbManager' },
  { id: SHORTCUT_GROUP_TERMINAL, labelKey: 'settings.shortcutGroupTerminal' },
];

export const SHORTCUT_COMMANDS = [
  {
    id: 'commandPalette',
    labelKey: 'settings.shortcutCommandPalette',
    defaultKeys: { mod: true, key: 'k' },
    editable: true,
    paletteActionId: null,
    group: SHORTCUT_GROUP_BASE,
  },
  // A second way into the palette (PLAN D1): opens it on recent items and
  // open tabs, like VS Code Quick Open. Also the escape hatch when Ctrl+K is
  // sent to the shell (Settings → Terminal).
  {
    id: 'quickOpen',
    labelKey: 'settings.shortcutQuickOpen',
    defaultKeys: { mod: true, key: 'p' },
    editable: true,
    paletteActionId: null,
    group: SHORTCUT_GROUP_BASE,
  },
  {
    id: 'newTab',
    labelKey: 'settings.shortcutNewRequest',
    defaultKeys: { mod: true, key: 't' },
    editable: true,
    paletteActionId: 'newTab',
    group: SHORTCUT_GROUP_BASE,
  },
  {
    id: 'toggleAiPanel',
    labelKey: 'settings.shortcutToggleAiPanel',
    defaultKeys: { mod: true, shift: true, key: 'a' },
    editable: true,
    paletteActionId: 'toggleAiPanel',
    group: SHORTCUT_GROUP_BASE,
  },
  {
    id: 'toggleDevTools',
    labelKey: 'settings.shortcutToggleDevTools',
    defaultKeys: { ctrl: true, key: '`' },
    editable: true,
    paletteActionId: 'toggleDevTools',
    group: SHORTCUT_GROUP_BASE,
  },
  {
    id: 'rename',
    labelKey: 'settings.shortcutRename',
    defaultKeys: { mod: true, key: 'e' },
    altKeys: { key: 'F2' },
    editable: true,
    paletteActionId: null,
    group: SHORTCUT_GROUP_BASE,
  },
  {
    id: 'duplicate',
    labelKey: 'settings.shortcutDuplicateRequest',
    defaultKeys: { mod: true, key: 'd' },
    editable: true,
    paletteActionId: null,
    group: SHORTCUT_GROUP_BASE,
  },
  // Display-only (handled inside RequestBuilder, not remappable).
  {
    id: 'sendRequest',
    labelKey: 'settings.shortcutSendRequest',
    defaultKeys: { mod: true, key: 'Enter' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_BASE,
  },
  {
    id: 'saveRequest',
    labelKey: 'settings.shortcutSaveRequest',
    defaultKeys: { mod: true, key: 's' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_BASE,
  },
  // ── DB Manager ─────────────────────────────────────────────────────────────
  // All fixed: each is handled inside DbManagerResultsTab, and several are
  // conditional on what the grid is doing (Escape means four different things
  // depending on state), so they are documented here rather than remappable.
  // Listing them anyway, because a shortcut nobody can discover may as well not
  // exist and Settings is where people look.
  {
    id: 'dbFindInResults',
    labelKey: 'settings.shortcutDbFind',
    defaultKeys: { mod: true, key: 'f' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbFindNext',
    labelKey: 'settings.shortcutDbFindNext',
    defaultKeys: { key: 'F3' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbFindPrev',
    labelKey: 'settings.shortcutDbFindPrev',
    defaultKeys: { shift: true, key: 'F3' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbRunStatement',
    labelKey: 'settings.shortcutDbRun',
    defaultKeys: { mod: true, key: 'Enter' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbSaveChanges',
    labelKey: 'settings.shortcutDbSave',
    defaultKeys: { mod: true, key: 's' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbFormatSql',
    labelKey: 'settings.shortcutDbFormat',
    defaultKeys: { shift: true, alt: true, key: 'f' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbQueryHistory',
    labelKey: 'settings.shortcutDbHistory',
    defaultKeys: { mod: true, shift: true, key: 'h' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbRecordView',
    labelKey: 'settings.shortcutDbRecordView',
    defaultKeys: { key: 'Tab' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbDuplicateRows',
    labelKey: 'settings.shortcutDbDuplicateRows',
    defaultKeys: { mod: true, key: 'd' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbCopyCells',
    labelKey: 'settings.shortcutDbCopyCells',
    defaultKeys: { mod: true, key: 'c' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbPasteCells',
    labelKey: 'settings.shortcutDbPasteCells',
    defaultKeys: { mod: true, key: 'v' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbSetNull',
    labelKey: 'settings.shortcutDbSetNull',
    defaultKeys: { alt: true, key: 'n' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  {
    id: 'dbEscape',
    labelKey: 'settings.shortcutDbEscape',
    defaultKeys: { key: 'Escape' },
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_DB_MANAGER,
  },
  // ── Terminal panes ─────────────────────────────────────────────────────────
  // Display-only: matched in components/terminal/terminalPaneShortcuts.js on
  // the PHYSICAL key (so they work on a German layout), only while focus is in
  // the Terminal. Windows/Linux mirror Windows Terminal, macOS mirrors iTerm2.
  ...terminalPaneRows(),
];

function terminalPaneRows() {
  const mac = isMac();
  const row = (id, labelKey, keys) => ({
    id,
    labelKey,
    defaultKeys: keys,
    editable: false,
    paletteActionId: null,
    group: SHORTCUT_GROUP_TERMINAL,
  });
  return [
    row('terminalSplitRight', 'settings.shortcutTerminalSplitRight', mac ? { meta: true, key: 'd' } : { alt: true, shift: true, key: '+' }),
    row('terminalSplitDown', 'settings.shortcutTerminalSplitDown', mac ? { meta: true, shift: true, key: 'd' } : { alt: true, shift: true, key: '-' }),
    row('terminalFocusPane', 'settings.shortcutTerminalFocusPane', mac ? { meta: true, alt: true, key: '←↑→↓' } : { alt: true, key: '←↑→↓' }),
    row('terminalResizePane', 'settings.shortcutTerminalResizePane', mac ? { meta: true, alt: true, shift: true, key: '←↑→↓' } : { alt: true, shift: true, key: '←↑→↓' }),
    row('terminalNextPane', 'settings.shortcutTerminalNextPane', mac ? { meta: true, key: ']' } : { alt: true, shift: true, key: 'o' }),
    row('terminalZoomPane', 'settings.shortcutTerminalZoomPane', mac ? { meta: true, shift: true, key: 'Enter' } : { ctrl: true, shift: true, key: 'Enter' }),
    row('terminalClosePane', 'settings.shortcutTerminalClosePane', mac ? { meta: true, shift: true, key: 'w' } : { ctrl: true, shift: true, key: 'w' }),
  ];
}

const COMMANDS_BY_ID = SHORTCUT_COMMANDS.reduce((acc, c) => {
  acc[c.id] = c;
  return acc;
}, {});

export function getShortcutCommand(id) {
  return COMMANDS_BY_ID[id] || null;
}

/** True when `e` matches a single keys spec. */
function matchesKeys(e, keys) {
  if (!keys || !keys.key) return false;
  const evKey = (e.key || '').toLowerCase();
  if (evKey !== String(keys.key).toLowerCase()) return false;

  if (keys.mod && !(e.ctrlKey || e.metaKey)) return false;
  if (keys.ctrl && !e.ctrlKey) return false;
  if (keys.meta && !e.metaKey) return false;
  // When no modifier is required at all, the key must be pressed bare.
  if (!keys.mod && !keys.ctrl && !keys.meta && (e.ctrlKey || e.metaKey)) return false;
  if (!!keys.shift !== !!e.shiftKey) return false;
  if (!!keys.alt !== !!e.altKey) return false;
  return true;
}

/**
 * True when the event matches the command's resolved binding (or its fixed altKeys).
 * Pass the command id and the user overrides map.
 */
export function matchesShortcut(e, id, overrides) {
  if (!e || e.isComposing) return false;
  const cmd = COMMANDS_BY_ID[id];
  if (!cmd) return false;
  const keys = resolveShortcutKeys(id, overrides);
  if (matchesKeys(e, keys)) return true;
  if (cmd.altKeys && matchesKeys(e, cmd.altKeys)) return true;
  return false;
}

/**
 * Resolve the active binding for a command:
 *   override present (object) -> custom; override present (null) -> unbound; absent -> default.
 */
export function resolveShortcutKeys(id, overrides) {
  const cmd = COMMANDS_BY_ID[id];
  if (!cmd) return null;
  if (overrides && Object.prototype.hasOwnProperty.call(overrides, id)) {
    return overrides[id];
  }
  return cmd.defaultKeys || null;
}

/** Two key specs describe the same combo. */
export function keysEqual(a, b) {
  if (!a || !b) return a === b;
  return (
    String(a.key).toLowerCase() === String(b.key).toLowerCase() &&
    !!a.mod === !!b.mod &&
    !!a.ctrl === !!b.ctrl &&
    !!a.meta === !!b.meta &&
    !!a.shift === !!b.shift &&
    !!a.alt === !!b.alt
  );
}

function isMac() {
  if (typeof navigator === 'undefined') return false;
  const platform = navigator.platform || '';
  const ua = navigator.userAgent || '';
  return /Mac|iPhone|iPad|iPod/i.test(platform) || /Mac OS X/i.test(ua);
}

function keyToken(key) {
  if (!key) return '';
  if (key.length === 1) return key.toUpperCase();
  return key;
}

/** Display tokens for a keys spec, e.g. ['⌘','T'] or ['Ctrl','Shift','A']. */
export function shortcutTokens(keys) {
  if (!keys || !keys.key) return [];
  const tokens = [];
  if (keys.mod) tokens.push(getModKeyLabel());
  if (keys.ctrl) tokens.push('Ctrl');
  if (keys.meta) tokens.push('⌘');
  if (keys.shift) tokens.push(isMac() ? '⇧' : 'Shift');
  if (keys.alt) tokens.push(isMac() ? '⌥' : 'Alt');
  tokens.push(keyToken(keys.key));
  return tokens;
}

/** Single string label for the palette `<kbd>`, e.g. '⌘ T' or 'Ctrl+T'. */
export function shortcutLabelString(keys) {
  const tokens = shortcutTokens(keys);
  if (!tokens.length) return '';
  return tokens.join(isMac() ? ' ' : '+');
}

const MODIFIER_KEYS = new Set(['Control', 'Shift', 'Alt', 'Meta', 'OS', 'AltGraph']);

/**
 * Build a keys spec from a captured keydown, or null if invalid (lone modifier,
 * or no primary modifier — we don't allow bare keys to avoid hijacking typing).
 */
export function keysFromEvent(e) {
  if (!e || MODIFIER_KEYS.has(e.key)) return null;
  const mod = e.ctrlKey || e.metaKey;
  const alt = e.altKey;
  if (!mod && !alt) return null;

  const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
  const spec = { key };
  if (mod) spec.mod = true;
  if (e.shiftKey) spec.shift = true;
  if (alt) spec.alt = true;
  return spec;
}

// --- Capture guard: while the Settings tab is recording a new combo, the global
// key handler must ignore keystrokes so they don't also fire actions. ---
let _capturing = false;
export function setCapturingShortcut(v) {
  _capturing = !!v;
}
export function isCapturingShortcut() {
  return _capturing;
}
